import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  inject,
  Injector,
  afterNextRender,
  viewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AclApdApiService,
  CatalogueApiService,
  FileApiService,
  SearchCriteria,
} from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';

import type { CanvasBboxSearchHit } from '../ogc/canvas-ogc-features-api.service';
import {
  type CanvasDataset,
  type CanvasDatasetAccess,
  CANVAS_OGC_QUERY_TYPES_SEARCH_CRITERION,
  extractCatalogueItemRecord,
  mapCatalogueItemToCanvasDataset,
  withCanvasPublishedCriterion,
  isCatalogueItemPublished,
} from '../../canvas-catalogue.util';
import {
  CanvasFilterMultiselectComponent,
  CanvasFilterOption,
} from '../datasets/canvas-filter-multiselect/canvas-filter-multiselect.component';
import { CanvasBodyPortalService } from '../../canvas-body-portal.service';
import {
  CANVAS_DATASET_DETAIL_URL,
  CANVAS_NOTIFIER,
  OGC_MAPS_RESOURCE_SERVER_URLS,
} from '../../../canvas-tokens';
import { isGrantedHasAccessResponse } from '../../normalize-acl-access-status';
import { CanvasDatasetLoadButtonsComponent } from '../dataset-load/canvas-dataset-load-buttons.component';
import { datasetMatchesCanvasQueryType } from '../../canvas-catalogue.util';
import { DatasetsModalComponent } from '../datasets/datasets-modal.component';
import { datasetCanLoadActions } from '../../canvas-catalogue.util';
import { isDatabankDownloadNotFoundError } from '../../canvas-download.util';
import { parseCatalogueDataDescriptors } from '../../../onboarding/dataset/catalogue-data-descriptor.util';
import type { CatalogueDataDescriptorViewModel } from '../../../onboarding/dataset/catalogue-data-descriptor.util';

const DEFAULT_CATALOGUE_TYPE = 'adex:DataBank';

/** Cached catalogue item payload from GET item API for a bbox hit. */
interface CatalogueHitCacheEntry {
  dataset: CanvasDataset;
  descriptors: CatalogueDataDescriptorViewModel[];
  organizationType: string;
}

export interface CanvasBboxEnrichedHit {
  hit: CanvasBboxSearchHit;
  dataset: CanvasDataset | null;
  name: string;
  provider: string;
  access: CanvasDatasetAccess;
  accessPolicyValue: string;
  tags: string[];
  organizationType: string;
  resourceApiType: string;
  crs: string;
  themes: string[];
}

@Component({
  selector: 'lib-canvas-bbox-results-modal',
  standalone: true,
  imports: [
    CommonModule,
    CanvasFilterMultiselectComponent,
    CanvasDatasetLoadButtonsComponent,
  ],
  templateUrl: './canvas-bbox-results-modal.component.html',
  styleUrl: './canvas-bbox-results-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CanvasBboxResultsModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() hits: CanvasBboxSearchHit[] = [];
  @Input() datasets: CanvasDataset[] = [];
  @Input() selectedResourceIds = new Set<string>();
  @Input() catalogueAssetType = DEFAULT_CATALOGUE_TYPE;

  @Output() closeModal = new EventEmitter<void>();
  @Output() startBboxFilter = new EventEmitter<CanvasDataset>();
  @Output() startGetAll = new EventEmitter<CanvasDataset>();
  @Output() startAttrFilter = new EventEmitter<CanvasDataset>();
  @Output() startStacList = new EventEmitter<CanvasDataset>();
  @Output() startLoadTiles = new EventEmitter<CanvasDataset>();

  readonly apiTypeOptionsModel = DatasetsModalComponent.API_TYPE_OPTIONS;

  private readonly injector = inject(Injector);
  private readonly bodyPortal = inject(CanvasBodyPortalService);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly aclApdApi = inject(AclApdApiService);
  private readonly fileApi = inject(FileApiService);
  private readonly keycloak = inject(KeycloakService);
  private readonly ogcServerUrls =
    inject(OGC_MAPS_RESOURCE_SERVER_URLS, { optional: true }) ?? [];
  private readonly bmPortalRoot =
    viewChild<ElementRef<HTMLElement>>('bmPortal');
  private readonly datasetDetailUrl =
    inject(CANVAS_DATASET_DETAIL_URL, { optional: true }) ??
    ((id: string) => `/dataset/${id}`);
  private readonly notifier = inject(CANVAS_NOTIFIER, { optional: true });
  private portalDetach?: () => void;
  private filtersLoadedForSession = false;

  expandedIds = signal<Set<string>>(new Set());
  noAccessDatasetIds = signal<Set<string>>(new Set());
  fileNotFoundDatasetIds = signal<Set<string>>(new Set());
  downloadBusyId = signal<string | null>(null);
  openFilterMultiselectKey = signal<string | null>(null);
  filtersLoading = signal(false);
  /** Catalogue item + dataDescriptor fetched via GET item for each bbox hit. */
  catalogueHitCache = signal<Map<string, CatalogueHitCacheEntry>>(new Map());
  catalogueEnrichLoading = signal(false);

  organisationOptions = signal<string[]>([]);
  tagsOptions = signal<string[]>([]);
  typesOptions = signal<string[]>([]);
  accessPolicyOptions = signal<{ label: string; value: string }[]>([]);
  crsOptions = signal<string[]>([]);
  themesOptions = signal<string[]>([]);

  selectedOrganisations = signal<string[]>([]);
  selectedTags = signal<string[]>([]);
  selectedTypes = signal<string[]>([]);
  selectedAccessPolicies = signal<string[]>([]);
  selectedCrs = signal<string[]>([]);
  selectedThemes = signal<string[]>([]);
  selectedApiTypes = signal<string[]>([]);

  readonly enrichedHits = computed<CanvasBboxEnrichedHit[]>(() => {
    const datasetById = new Map(this.datasets.map((d) => [d.id, d]));
    for (const [id, entry] of this.catalogueHitCache()) {
      if (!datasetById.has(id)) {
        datasetById.set(id, entry.dataset);
      }
    }
    return this.hits.map((hit) => {
      const dataset =
        datasetById.get(hit.resourceId) ??
        this.datasets.find((d) =>
          d.resourceServerUrl
            ? hit.resourceId === d.id ||
              d.resourceServerUrl
                .toLowerCase()
                .includes(hit.resourceId.toLowerCase())
            : false,
        ) ??
        null;
      const props = hit.properties ?? {};
      const access = dataset?.access ?? this.inferAccessFromProperties(props);
      const accessPolicyValue = this.resolveAccessPolicyValue(
        dataset,
        access,
        props,
      );
      return {
        hit,
        dataset,
        name: dataset?.name ?? hit.label,
        provider:
          dataset?.provider ??
          String(
            props['organization'] ??
              props['provider'] ??
              props['organisation'] ??
              '',
          ),
        access,
        accessPolicyValue,
        tags: this.extractStringList(props['keywords'] ?? props['tags']),
        organizationType: this.resolveOrganizationType(
          hit.resourceId,
          props,
        ),
        resourceApiType: String(
          dataset?.resourceApiType ??
            props['ogcResourceAPIs'] ??
            'FEATURES',
        ).trim(),
        crs: String(props['crs'] ?? props['srid'] ?? '').trim(),
        themes: this.extractStringList(props['concepts'] ?? props['themes']),
      };
    });
  });

  organisationOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.organisationOptions().map((o) => ({ label: o, value: o })),
  );
  tagsOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.tagsOptions().map((o) => ({ label: o, value: o })),
  );
  typesOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.typesOptions().map((o) => ({ label: o, value: o })),
  );
  accessPolicyOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.accessPolicyOptions().map((o) => ({ label: o.label, value: o.value })),
  );
  crsOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.crsOptions().map((o) => ({ label: o, value: o })),
  );
  themesOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.themesOptions().map((o) => ({ label: o, value: o })),
  );

  hasActiveFilters = computed(
    () =>
      this.selectedOrganisations().length > 0 ||
      this.selectedTags().length > 0 ||
      this.selectedTypes().length > 0 ||
      this.selectedAccessPolicies().length > 0 ||
      this.selectedCrs().length > 0 ||
      this.selectedThemes().length > 0 ||
      this.selectedApiTypes().length > 0,
  );

  readonly filteredHits = computed(() => {
    const orgFilter = this.selectedOrganisations();
    const tagFilter = this.selectedTags();
    const typeFilter = this.selectedTypes();
    const apiTypeFilter = this.selectedApiTypes();
    const accessFilter = this.selectedAccessPolicies();
    const crsFilter = this.selectedCrs();
    const themeFilter = this.selectedThemes();
    const catalogueCache = this.catalogueHitCache();
    const enriching = this.catalogueEnrichLoading();

    return this.enrichedHits().filter((row) => {
      // After catalogue enrichment, only show verified published datasets.
      if (!enriching && this.hits.length > 0 && !catalogueCache.has(row.hit.resourceId)) {
        return false;
      }

      if (orgFilter.length > 0 && !orgFilter.includes(row.provider)) {
        return false;
      }
      if (
        tagFilter.length > 0 &&
        !row.tags.some((t) => tagFilter.includes(t))
      ) {
        return false;
      }
      if (typeFilter.length > 0 && !typeFilter.includes(row.organizationType)) {
        return false;
      }
      if (
        apiTypeFilter.length > 0 &&
        (!row.dataset ||
          !apiTypeFilter.some((t) =>
            datasetMatchesCanvasQueryType(row.dataset!, t),
          ))
      ) {
        return false;
      }
      if (
        accessFilter.length > 0 &&
        !this.matchesAccessPolicy(row, accessFilter)
      ) {
        return false;
      }
      if (crsFilter.length > 0 && (!row.crs || !crsFilter.includes(row.crs))) {
        return false;
      }
      if (
        themeFilter.length > 0 &&
        !row.themes.some((t) => themeFilter.includes(t))
      ) {
        return false;
      }
      return true;
    });
  });

  ngOnChanges(changes: SimpleChanges): void {
    const isOpenCh = changes['isOpen'];
    if (isOpenCh) {
      if (isOpenCh.currentValue === true) {
        this.expandedIds.set(new Set());
        if (!this.filtersLoadedForSession) {
          void this.loadCatalogueFilters();
        }
        void this.enrichCatalogueDatasetsForHits();
        afterNextRender(
          () => {
            if (!this.isOpen) return;
            const root = this.bmPortalRoot()?.nativeElement;
            if (!root?.parentElement) return;
            this.portalDetach?.();
            this.portalDetach = this.bodyPortal.attach(root);
          },
          { injector: this.injector },
        );
      } else {
        this.detachModalPortal();
      }
    }
    if (changes['hits'] && this.isOpen) {
      this.collapseExpandedCards();
      void this.enrichCatalogueDatasetsForHits();
    }
    if (changes['isOpen'] && !this.isOpen) {
      this.resetPanelState();
    }
    if (
      changes['catalogueAssetType'] &&
      !changes['catalogueAssetType'].firstChange
    ) {
      this.filtersLoadedForSession = false;
      if (this.isOpen) {
        void this.loadCatalogueFilters();
      }
    }
  }

  ngOnDestroy(): void {
    this.detachModalPortal();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('cv-dm-backdrop')) {
      this.closeModal.emit();
    }
  }

  onFilterMultiselectExclusiveChange(ev: { key: string; open: boolean }): void {
    if (ev.open) {
      this.openFilterMultiselectKey.set(ev.key);
    } else if (this.openFilterMultiselectKey() === ev.key) {
      this.openFilterMultiselectKey.set(null);
    }
  }

  onOrganisationsFilterChange(values: string[]): void {
    this.collapseExpandedCards();
    this.selectedOrganisations.set(values);
  }

  onTagsFilterChange(values: string[]): void {
    this.collapseExpandedCards();
    this.selectedTags.set(values);
  }

  onTypesFilterChange(values: string[]): void {
    this.collapseExpandedCards();
    this.selectedTypes.set(values);
  }

  onAccessPoliciesFilterChange(values: string[]): void {
    this.collapseExpandedCards();
    this.selectedAccessPolicies.set(values);
  }

  onCrsFilterChange(values: string[]): void {
    this.collapseExpandedCards();
    this.selectedCrs.set(values);
  }

  onThemesFilterChange(values: string[]): void {
    this.collapseExpandedCards();
    this.selectedThemes.set(values);
  }

  onApiTypesFilterChange(values: string[]): void {
    this.collapseExpandedCards();
    this.selectedApiTypes.set(values);
  }

  clearAllFilters(): void {
    if (!this.hasActiveFilters()) {
      return;
    }
    this.collapseExpandedCards();
    this.openFilterMultiselectKey.set(null);
    this.selectedOrganisations.set([]);
    this.selectedTags.set([]);
    this.selectedTypes.set([]);
    this.selectedAccessPolicies.set([]);
    this.selectedCrs.set([]);
    this.selectedThemes.set([]);
    this.selectedApiTypes.set([]);
  }

  toggleExpand(id: string): void {
    this.expandedIds.update((set) => {
      if (set.has(id)) {
        return new Set();
      }
      return new Set([id]);
    });
  }

  isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }

  private collapseExpandedCards(): void {
    this.expandedIds.set(new Set());
  }

  isDownloadBusy(resourceId: string): boolean {
    return this.downloadBusyId() === resourceId;
  }

  showsNoAccessMessage(resourceId: string): boolean {
    return this.noAccessDatasetIds().has(resourceId);
  }

  showsFileNotFoundMessage(resourceId: string): boolean {
    return this.fileNotFoundDatasetIds().has(resourceId);
  }

  /** Dataset for load actions — catalogue-enriched or built from hit metadata. */
  resolveLoadDataset(row: CanvasBboxEnrichedHit): CanvasDataset {
    const cached = this.catalogueHitCache().get(row.hit.resourceId);
    if (row.dataset) {
      return row.dataset;
    }
    if (cached) {
      return cached.dataset;
    }
    const fallbackServer = this.ogcServerUrls
      .map((u) => u.trim())
      .find(Boolean);
    return {
      id: row.hit.resourceId,
      name: row.name,
      provider: row.provider,
      access: row.access,
      accessPolicy: row.accessPolicyValue,
      ogcResourceApis: ['FEATURES'],
      resourceApiType: 'FEATURES',
      ...(fallbackServer
        ? { resourceServerUrl: fallbackServer.replace(/\/+$/, '') }
        : {}),
    };
  }

  canLoadDataset(dataset: CanvasDataset): boolean {
    return datasetCanLoadActions(dataset);
  }

  onBboxFilterClick(dataset: CanvasDataset): void {
    if (!dataset.resourceServerUrl) {
      this.notifier?.error(
        'This dataset has no OGC Features resource server URL.',
      );
      return;
    }
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startBboxFilter.emit(dataset);
      this.closeModal.emit();
    });
  }

  onGetAllClick(dataset: CanvasDataset): void {
    if (!dataset.resourceServerUrl) {
      this.notifier?.error(
        'This dataset has no OGC Features resource server URL.',
      );
      return;
    }
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startGetAll.emit(dataset);
      this.closeModal.emit();
    });
  }

  onAttrFilterClick(dataset: CanvasDataset): void {
    if (!dataset.resourceServerUrl) {
      this.notifier?.error(
        'This dataset has no OGC Features resource server URL.',
      );
      return;
    }
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startAttrFilter.emit(dataset);
      this.closeModal.emit();
    });
  }

  onStacListClick(dataset: CanvasDataset): void {
    if (!dataset.stacServerUrl && !dataset.resourceServerUrl) {
      this.notifier?.error('This dataset has no STAC resource server URL.');
      return;
    }
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startStacList.emit(dataset);
      this.closeModal.emit();
    });
  }

  onLoadTilesClick(dataset: CanvasDataset): void {
    if (!dataset.tileServerUrl) {
      this.notifier?.error('This dataset has no tile resource server URL.');
      return;
    }
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startLoadTiles.emit(dataset);
      this.closeModal.emit();
    });
  }

  async onDownloadClick(
    row: CanvasBboxEnrichedHit,
    event: Event,
  ): Promise<void> {
    event.stopPropagation();
    if (!row.dataset || this.downloadBusyId()) return;

    const dataset = row.dataset;
    this.downloadBusyId.set(row.hit.resourceId);
    this.clearNoAccessMessage(row.hit.resourceId);
    this.clearFileNotFoundMessage(row.hit.resourceId);

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.setNoAccessMessage(row.hit.resourceId);
        return;
      }

      const hasAccess = await this.userHasDatasetAccess(dataset);
      if (!hasAccess) {
        this.setNoAccessMessage(row.hit.resourceId);
        return;
      }

      await this.triggerDatasetDownload(dataset.id);
    } catch (err: unknown) {
      if (isDatabankDownloadNotFoundError(err)) {
        this.setFileNotFoundMessage(row.hit.resourceId);
      } else {
        this.notifier?.error('Failed to download file.');
      }
    } finally {
      this.downloadBusyId.set(null);
    }
  }

  onRequestAccessClick(resourceId: string, event: Event): void {
    event.stopPropagation();
    this.notifier?.success(
      'Open Geospatial Datasets to request access for this resource.',
    );
    void resourceId;
  }

  openDatasetDetail(resourceId: string, event: Event): void {
    event.stopPropagation();
    const url = this.datasetDetailUrl(resourceId);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private matchesAccessPolicy(
    row: CanvasBboxEnrichedHit,
    selected: string[],
  ): boolean {
    const value = row.accessPolicyValue.toLowerCase();
    const accessLabel = row.access.toLowerCase();
    return selected.some((s) => {
      const key = s.toLowerCase();
      return (
        key === value ||
        key === accessLabel ||
        this.accessLabelToPolicy(row.access) === key
      );
    });
  }

  private accessLabelToPolicy(access: CanvasDatasetAccess): string {
    switch (access) {
      case 'Public':
        return 'open';
      case 'Restricted':
        return 'restricted';
      default:
        return 'pii';
    }
  }

  private resolveAccessPolicyValue(
    dataset: CanvasDataset | null,
    access: CanvasDatasetAccess,
    props: Record<string, unknown>,
  ): string {
    if (dataset?.accessPolicy) {
      return dataset.accessPolicy.toLowerCase();
    }
    const raw = String(
      props['accessPolicy'] ?? props['access'] ?? '',
    ).toLowerCase();
    if (raw) return raw;
    return this.accessLabelToPolicy(access);
  }

  /**
   * Organisation type from catalogue enrichment or OGC record properties only.
   * Do not fall back to `type` / `resourceType` — those are asset kinds (e.g. "dataset").
   */
  private resolveOrganizationType(
    resourceId: string,
    props: Record<string, unknown>,
  ): string {
    const cached = this.catalogueHitCache().get(resourceId);
    if (cached?.organizationType) {
      return cached.organizationType;
    }
    return String(
      props['organizationType'] ?? props['organisationType'] ?? '',
    ).trim();
  }

  private extractStringList(raw: unknown): string[] {
    if (!Array.isArray(raw)) {
      const single = String(raw ?? '').trim();
      return single ? [single] : [];
    }
    return [...new Set(raw.map((x) => String(x).trim()).filter(Boolean))];
  }

  private buildOgcScopedSearchCriteria(): SearchCriteria[] {
    return withCanvasPublishedCriterion([
      { searchType: 'term', field: 'type', values: [this.catalogueAssetType] },
      ...this.buildOgcResourceServerCriteria(),
      CANVAS_OGC_QUERY_TYPES_SEARCH_CRITERION,
    ]);
  }

  private buildOgcResourceServerCriteria(): SearchCriteria[] {
    const ogcUrls = this.ogcServerUrls.map((u) => u.trim()).filter(Boolean);
    if (ogcUrls.length === 0) {
      return [];
    }
    return [
      {
        searchType: 'flattenedTerm',
        field: 'resourceServer.url',
        values: ogcUrls,
      },
    ];
  }

  private async loadCatalogueFilters(): Promise<void> {
    this.filtersLoading.set(true);
    try {
      const filterFields = [
        'organization',
        'tags',
        'organizationType',
        'accessPolicy',
        'concepts',
        'resourceServer.url',
        'crs',
      ];

      const response = await this.catalogueApi.getFilterList(
        this.buildOgcScopedSearchCriteria(),
        filterFields,
        {
          isLoaderRequired: false,
          isToasterRequired: true,
        },
      );

      const apiData = this.normalizeCatalogListResult(response);
      if (!apiData) {
        return;
      }

      this.organisationOptions.set(
        this.extractScalarList(apiData, 'organization'),
      );
      this.tagsOptions.set(this.extractScalarList(apiData, 'tags'));
      this.typesOptions.set(
        this.extractScalarList(apiData, 'organizationType'),
      );
      this.themesOptions.set(this.extractScalarList(apiData, 'concepts'));
      this.crsOptions.set(this.extractScalarList(apiData, 'crs'));

      const rawPolicies = this.extractScalarList(apiData, 'accessPolicy');
      const labelMap: Record<string, string> = {
        open: 'Public',
        restricted: 'Restricted',
        pii: 'Personal',
      };
      this.accessPolicyOptions.set(
        rawPolicies.map((v) => ({
          value: v,
          label: labelMap[String(v).toLowerCase()] ?? v,
        })),
      );

      this.filtersLoadedForSession = true;
    } catch {
      // Toaster handled by API layer when enabled
    } finally {
      this.filtersLoading.set(false);
    }
  }

  private normalizeCatalogListResult(
    response: unknown,
  ): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') return null;
    const r = response as Record<string, unknown>;
    const result = r['result'];
    if (result == null) return null;

    if (
      Array.isArray(result) &&
      result.length > 0 &&
      typeof result[0] === 'object'
    ) {
      const merged: Record<string, unknown[]> = {};
      for (const item of result as Record<string, unknown>[]) {
        for (const [key, val] of Object.entries(item)) {
          if (!merged[key]) merged[key] = [];
          if (Array.isArray(val)) {
            for (const x of val) {
              if (x != null && x !== '') merged[key].push(x);
            }
          } else if (val != null && val !== '') {
            merged[key].push(val);
          }
        }
      }
      const deduped: Record<string, unknown> = {};
      for (const [key, vals] of Object.entries(merged)) {
        deduped[key] = [...new Set(vals.map(String))];
      }
      return deduped;
    }

    if (typeof result === 'object' && !Array.isArray(result)) {
      return result as Record<string, unknown>;
    }

    return null;
  }

  private extractScalarList(
    data: Record<string, unknown>,
    field: string,
  ): string[] {
    const parts = field.split('.');
    let cur: unknown = data;
    for (const p of parts) {
      cur = (cur as Record<string, unknown>)?.[p];
    }

    if (Array.isArray(cur)) {
      return [...new Set(cur.filter((x) => x != null && x !== '').map(String))];
    }
    if (cur != null && cur !== '') {
      return [String(cur)];
    }
    return [];
  }

  private async userHasDatasetAccess(dataset: CanvasDataset): Promise<boolean> {
    const policy = String(dataset.accessPolicy ?? '').toLowerCase();
    if (policy === 'open' || policy === 'public') {
      return true;
    }

    try {
      const response = await this.aclApdApi.hasAccess({ itemId: dataset.id });
      return isGrantedHasAccessResponse(response, true);
    } catch (err: unknown) {
      return isGrantedHasAccessResponse(err, false);
    }
  }

  /** Login + has_access for restricted datasets. Keeps the modal open on deny. */
  private async runRestrictedDatasetAction(
    dataset: CanvasDataset,
    onAllowed: () => void,
  ): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.setNoAccessMessage(dataset.id);
        return;
      }

      const hasAccess = await this.userHasDatasetAccess(dataset);
      if (!hasAccess) {
        this.setNoAccessMessage(dataset.id);
        return;
      }

      this.clearNoAccessMessage(dataset.id);
      onAllowed();
    } catch {
      // Access check failed — user can retry.
    }
  }

  private async triggerDatasetDownload(datasetId: string): Promise<void> {
    const { downloadUrl } =
      await this.fileApi.getDatabankDownloadUrl(datasetId);
    if (!downloadUrl) {
      throw new Error('Download URL not available');
    }
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  }

  private setNoAccessMessage(resourceId: string): void {
    this.clearFileNotFoundMessage(resourceId);
    this.noAccessDatasetIds.update((set) => new Set(set).add(resourceId));
  }

  private clearNoAccessMessage(resourceId: string): void {
    this.noAccessDatasetIds.update((set) => {
      const next = new Set(set);
      next.delete(resourceId);
      return next;
    });
  }

  private setFileNotFoundMessage(resourceId: string): void {
    this.clearNoAccessMessage(resourceId);
    this.fileNotFoundDatasetIds.update((set) => new Set(set).add(resourceId));
  }

  private clearFileNotFoundMessage(resourceId: string): void {
    this.fileNotFoundDatasetIds.update((set) => {
      const next = new Set(set);
      next.delete(resourceId);
      return next;
    });
  }

  private inferAccessFromProperties(
    properties?: Record<string, unknown>,
  ): CanvasDatasetAccess {
    const policy = String(
      properties?.['accessPolicy'] ?? properties?.['access'] ?? '',
    ).toLowerCase();
    if (policy === 'open' || policy === 'public') return 'Public';
    if (policy === 'restricted') return 'Restricted';
    return 'Private';
  }

  private async enrichCatalogueDatasetsForHits(): Promise<void> {
    const hitIds = [
      ...new Set(this.hits.map((h) => h.resourceId).filter(Boolean)),
    ];
    if (hitIds.length === 0) {
      return;
    }

    const missingIds = hitIds.filter((id) => !this.catalogueHitCache().has(id));
    if (missingIds.length === 0) {
      return;
    }

    this.catalogueEnrichLoading.set(true);
    try {
      const results = await Promise.allSettled(
        missingIds.map((id) => this.catalogueApi.getItemById(id)),
      );
      const next = new Map(this.catalogueHitCache());
      for (let i = 0; i < missingIds.length; i++) {
        const result = results[i];
        if (result.status !== 'fulfilled') {
          continue;
        }
        const record = extractCatalogueItemRecord(result.value);
        if (!record || !isCatalogueItemPublished(record)) {
          continue;
        }
        const dataset = mapCatalogueItemToCanvasDataset(record);
        if (!dataset) {
          continue;
        }
        const descriptors = parseCatalogueDataDescriptors(
          record['dataDescriptor'],
        );
        const organizationType = String(
          record['organizationType'] ?? record['organisationType'] ?? '',
        ).trim();
        next.set(missingIds[i], { dataset, descriptors, organizationType });
      }
      this.catalogueHitCache.set(next);
    } finally {
      this.catalogueEnrichLoading.set(false);
    }
  }

  private resetPanelState(): void {
    this.expandedIds.set(new Set());
    this.noAccessDatasetIds.set(new Set());
    this.fileNotFoundDatasetIds.set(new Set());
    this.downloadBusyId.set(null);
    this.openFilterMultiselectKey.set(null);
    this.catalogueHitCache.set(new Map());
    this.catalogueEnrichLoading.set(false);
    this.resetFilterOptions();
  }

  private resetFilterOptions(): void {
    this.filtersLoadedForSession = false;
    this.organisationOptions.set([]);
    this.tagsOptions.set([]);
    this.typesOptions.set([]);
    this.accessPolicyOptions.set([]);
    this.crsOptions.set([]);
    this.themesOptions.set([]);
    this.selectedOrganisations.set([]);
    this.selectedTags.set([]);
    this.selectedTypes.set([]);
    this.selectedAccessPolicies.set([]);
    this.selectedCrs.set([]);
    this.selectedThemes.set([]);
    this.selectedApiTypes.set([]);
  }

  private detachModalPortal(): void {
    this.portalDetach?.();
    this.portalDetach = undefined;
  }
}
